import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'reactstrap';
import { Link } from 'react-router-dom'
import { getModules } from '../../store/ModuleAction';
import * as Helper from '../../util/Helper';
import StatusCode from '../../util/StatusCode';
import ModulePresentation from './components/ModulePresentation';
import TableHorizontal, { InfoObject } from '../misc/TableHorizontal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { subscribe } from '../../store/NotificationAction';
import { useTranslation } from 'react-i18next';
import { CoreModule, CoreModuleTrigger, ModuleType } from '../../models/CoreModels';
import * as Notifications from '../../services/Notifications'
import { RequestState } from '../../models/RequestState';
import { RootState } from '../../store/store';
import ModuleSyncStatus from '../misc/ModuleSyncStatus';
import ModuleStatusSeverityIcon from '../misc/ModuleStatusSeverityIcon';

const ModuleList = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation(['common']);

    const [isOpen, setOpen] = useState(false);
    const [lastMessageId, setMessageId] = useState<string | undefined>(undefined);

    const modules = useSelector<RootState, RequestState<CoreModule[]>>(state => state.module.modules)
    const notification = useSelector<RootState, Partial<Notifications.NotificationServiceMessage>>(state => state.notification);

    useEffect(() => {
        getModules(false)(dispatch);
        return subscribe(['moduleInstanceStartRequest', 'moduleInstanceStart', 'moduleInstancePhaseUpdate', 'moduleInstanceComplete'])(dispatch);
    }, [dispatch]);

    useEffect(() => {
        if (modules.code === StatusCode.COMPLETE && lastMessageId !== notification.messageId) {
            setMessageId(notification.messageId);
            getModules(true)(dispatch);
        }
    }, [modules.code, dispatch, notification.messageId, lastMessageId])

    const getModuleTypeName = (moduleType: ModuleType) => {
        switch (moduleType) {
            case ModuleType.ImportModule: return t("modules.import");
            case ModuleType.ExportModule: return t("modules.export");
            default: return t("modules.unknown");
        }
    }

    const renderTriggers = (moduleTriggers: CoreModuleTrigger[] | undefined, nextScheduledStart: string | undefined) => {
        if (!moduleTriggers) {
            return "-";
        }
        if (moduleTriggers.length > 1) {
            return t("modules.triggers.multiple");
        } else if (moduleTriggers.length === 1) {
            if (moduleTriggers[0].triggerType === 2) {
                return (<span>{t('modules.triggers.after')} <Link to={`/module/${moduleTriggers[0].runAfterModuleId}/configuration`}>{moduleTriggers[0].runAfterModuleTitle}</Link></span>);
            } else if (moduleTriggers[0].triggerType === 1) {
                if (nextScheduledStart) {
                    return Helper.formatDate(nextScheduledStart);
                }
                return t("modules.triggers.time");
            }
        }
    }

    const renderMyModules = () => {
        let modulesList: InfoObject[] = [];
        if (modules.code === StatusCode.COMPLETE) {
            modulesList = modules.data?.map(m => ({
                id: m.id,
                data: [
                    m.runnable ? { value: <Link to={`/module/${m.id}/status`}>{m.title}</Link> } : { value: m.title },
                    { value: <div className="list_icon">  <ModuleStatusSeverityIcon status={m.status} /> </div> },
                    {
                        value:<ModuleSyncStatus
                            status={m.status}
                            displayQuarantine={false}
                            runnable={m.runnable}
                            />
                    },
                    { value: Helper.active(m.active) },
                    { value: t(`modules.${m.moduleTypeName}.title`) },
                    { value: getModuleTypeName(m.moduleType) },
                    { value: renderTriggers(m.moduleTriggers, m.nextScheduledStart) },
                    { value: <Link to={`/module/${m.id}/configuration`}><FontAwesomeIcon color="#7b8898" icon="cog" className="icon_active" /></Link>, className: "ml-right icon_hover" },
                ]
            })) ?? [];
        }

        return (
            <TableHorizontal
                header={[
                    { value: t('modules.title') },
                    { value: t('modules.status.tableHeader')},
                    { value: '' },
                    { value: t('status.active') },
                    { value: t('modules.moduleType') },
                    { value: t('modules.direction') },
                    { value: t('modules.triggers.tableHeader') },
                    { value: t('modules.configuration') }
                ]}
                info={modulesList}
                error={modules.code === StatusCode.ERROR}
                searching={modules.code === StatusCode.PENDING}
                noResMsg={t('error.getModules')}
            />
        )
    }

    return <div className="container list-module">
        <div className="header--fixed">
            <h1>{t('modules.modules')}</h1>
            <div>
                <Button color="primary" onClick={() => setOpen(true)}>{t("modules.createNew")}</Button>
                <ModulePresentation
                    isOpen={isOpen}
                    toggleModal={() => setOpen(!isOpen)}
                />
            </div>
        </div>
        <div className="body--scroll padding-40">
            {renderMyModules()}
        </div>
    </div>
}

export default ModuleList;
